<template>
  <layout-loading v-if="!itemLoaded" />
  <div v-else class="loanable-form">
    <loanable-type-selection
      v-if="!item.id && !item.type"
      :available-types="ownerAvailableTypes"
      :loanable="item"
      :owner-user="ownerUser"
    />

    <validation-observer v-else ref="observer" v-slot="{ validate }">
      <!--
        Submit is bound to buttons directly so as to prevent vue-cal to
        trigger a submit event for an unknown reason.
      -->
      <availability-status :loanable="item" />
      <b-form :novalidate="true" class="form loanable-form__form" @submit.stop.prevent>
        <form-section
          toggleable
          :force-show="openSections"
          class="mt-2"
          :section-title="$t('components.loanable.form.basic_info')"
          :inititally-visible="!item.published"
        >
          <b-row>
            <b-col lg="8">
              <forms-validated-input
                v-model="item.name"
                name="name"
                :label="$t('loanables.fields.name') | capitalize"
                :rules="form.general.name.rules"
                type="text"
                :placeholder="placeholderOrLabel('name', 'loanables') | capitalize"
                :description="$t('loanables.descriptions.name')"
              />
            </b-col>

            <b-col lg="4">
              <forms-validated-input
                v-model="item.type"
                name="type"
                :label="$t('loanables.fields.type') | capitalize"
                :rules="form.general.type.rules"
                type="select"
                :options="typeOptions"
                :placeholder="placeholderOrLabel('type', 'loanables') | capitalize"
                :disabled="!!item.id"
                disabled-tooltip="$t('components.loanable.form.disabled_type_tooltip')"
              />
            </b-col>
          </b-row>

          <b-row>
            <b-col lg="8">
              <!-- If we want to limit the position to the community area, we can added the
               'bounded' attribute to this input. -->
              <forms-validated-input
                v-model="item.position"
                name="position"
                :description="$t('components.loanable.form.position_description')"
                :rules="{
                  required: item.published || validatePublishing,
                }"
                :label="$t('loanables.fields.position') | capitalize"
                type="point"
                :placeholder="placeholderOrLabel('position', 'loanables') | capitalize"
                :polygons="loanablePolygons"
              />
            </b-col>

            <b-col>
              <forms-validated-input
                v-model="item.location_description"
                name="location_description"
                :description="$t('loanables.descriptions.location_description')"
                :rules="form.general.location_description.rules"
                :rows="12"
                :label="$t('loanables.fields.location_description') | capitalize"
                type="textarea"
                :placeholder="placeholderOrLabel('location_description', 'loanables') | capitalize"
              />
            </b-col>
          </b-row>

          <forms-validated-input
            v-model="item.images"
            type="images"
            name="image"
            :label="$t('loanables.fields.images') | capitalize"
            :preview-aspect-ratio="'16 / 10'"
            :description="$t('loanables.descriptions.images')"
            main-image-field="main_image"
            :rules="{
              required: item.published || validatePublishing,
            }"
          />
          <b-row>
            <b-col>
              <forms-validated-input
                v-model="item.comments"
                name="comments"
                :description="$t('loanables.descriptions.comments')"
                :rules="form.general.comments.rules"
                :label="$t('loanables.fields.comments') | capitalize"
                type="markdown"
                :placeholder="placeholderOrLabel('comments', 'loanables') | capitalize"
                :show-preview-initially="!!item.comments"
              />
            </b-col>
          </b-row>
        </form-section>

        <form-section
          :section-title="$t('components.loanable.form.sharing')"
          :force-show="openSections"
          toggleable
          :inititally-visible="!item.published"
        >
          <forms-validated-input
            v-model="item.min_loan_duration_in_minutes"
            type="duration"
            name="min_loan_duration_in_minutes"
            :label="$t('loanables.loan_duration.min.label')"
          />

          <forms-validated-input
            v-model="item.max_loan_duration_in_minutes"
            type="duration"
            name="max_loan_duration_in_minutes"
            :label="$t('loanables.loan_duration.max.label')"
          />

          <forms-validated-input
            v-model="item.instructions"
            name="instructions"
            :description="$t('loanables.descriptions.instructions')"
            :rules="form.general.instructions.rules"
            :label="$t('loanables.fields.instructions') | capitalize"
            type="markdown"
            :placeholder="placeholderOrLabel('instructions', 'loanables') | capitalize"
            :show-preview-initially="!!item.instructions"
          />

          <forms-validated-input
            v-model="item.return_instructions"
            name="return_instructions"
            :description="$t('loanables.descriptions.return_instructions')"
            :label="$t('loanables.fields.return_instructions') | capitalize"
            type="markdown"
            :placeholder="placeholderOrLabel('return_instructions', 'loanables') | capitalize"
            :show-preview-initially="!!item.return_instructions"
          />

          <forms-validated-input
            v-if="item.type === 'car'"
            v-model="item.details.has_onboard_notebook"
            type="checkbox"
            name="has_onboard_notebook"
            :label="$t('components.loanable.form.has_onboard_notebook')"
            :rules="{
              ...carForm.has_onboard_notebook.rules,
              accepted: item.published || validatePublishing,
            }"
          >
            <template v-if="guideUrl" #description>
              <a :href="guideUrl" target="_blank">
                {{ $t("components.loanable.form.onboard_notebook_guide") }}
              </a>
            </template>
          </forms-validated-input>

          <forms-validated-input
            v-model="item.shared_publicly"
            type="checkbox"
            name="shared_publicly"
            :label="$t('components.loanable.form.shared_publicly')"
            :description="$t('components.loanable.form.shared_publicly_description')"
          />

          <div class="loanable-form__community-sharing">
            <label class="d-block">{{
              $t("components.loanable.form.shareable_targets.title")
            }}</label>
            <p class="text-muted">
              {{ $t("components.loanable.form.shareable_targets.description") }}
            </p>

            <b-list-group v-if="shareableTargets.length > 0">
              <b-list-group-item
                v-for="target in shareableTargets"
                :key="`${target.type}-${target.id}`"
                class="loanable-form__community-sharing__item"
              >
                <div>
                  <strong>{{ target.name }}</strong>
                  <b-badge
                    class="ml-2"
                    :variant="target.type === 'library' ? 'primary' : 'secondary'"
                  >
                    {{
                      target.type === "library"
                        ? $t("components.loanable.form.shareable_targets.types.library")
                        : $t("components.loanable.form.shareable_targets.types.community")
                    }}
                  </b-badge>
                </div>

                <b-form-checkbox
                  v-if="target.type === 'community'"
                  :checked="target.shared"
                  switch
                  @change="setShareableCommunityShared(target.id, $event)"
                >
                  {{ $t("components.loanable.form.shareable_targets.shared") }}
                </b-form-checkbox>

                <span v-if="target.type === 'library'" class="text-muted small">
                  {{ $t("components.loanable.form.shareable_targets.in_library") }}
                </span>
              </b-list-group-item>
            </b-list-group>

            <b-alert v-else variant="info" show>
              {{ $t("components.loanable.form.shareable_targets.empty") }}
            </b-alert>
          </div>

          <forms-validated-input
            v-if="showSelfServiceInput"
            v-model="item.sharing_mode"
            type="select"
            :options="sharingModeOptions"
            name="sharing_mode"
            :label="$t('loanables.fields.sharing_mode.title') | capitalize"
            :description="$t('loanables.fields.sharing_mode.description') | capitalize"
          />

          <conditional-context
            v-if="item.sharing_mode === 'hybrid'"
            show
            :label="$t('components.loanable.form.hybrid_options')"
          >
            <forms-validated-input
              v-model="item.trusted_borrower_instructions"
              name="trusted_borrower_instructions"
              :description="$t('loanables.descriptions.trusted_borrower_instructions')"
              :label="$t('loanables.fields.trusted_borrower_instructions') | capitalize"
              type="markdown"
              :placeholder="
                placeholderOrLabel('trusted_borrower_instructions', 'loanables') | capitalize
              "
              :show-preview-initially="!!item.trusted_borrower_instructions"
            />

            <b-alert v-if="!ownerUser || !item.id" variant="info" show>
              {{ $t("components.loanable.form.save_first") }}
            </b-alert>
            <paginated-table
              v-else
              ref="trustedBorrowerTable"
              :endpoint="`/loanables/${item.id}/roles`"
              :columns="trustedBorrowersColumns"
              :show-generate-csv="false"
              :load-relations="['user']"
              :fetch-params="{
                role: 'trusted_borrower',
              }"
              :row-disabled="(item) => item.ressource_type === 'library'"
            >
              <template #head>
                <h4 class="m-lg-0">
                  <a id="trusted_borrowers" href="#trusted_borrowers"></a>
                  <b-icon class="text-primary" icon="shield-fill-check" />&nbsp;
                  {{ $t("components.loanable.form.trusted_network") }}
                </h4>
              </template>
              <template #head-buttons>
                <query-select
                  ref="trustedBorrowerSelect"
                  :placeholder="$t('components.loanable.form.add_member')"
                  :search-fct="assignableUsersToRoleSearchFct"
                  class="add-user-input"
                  name="new_trusted_member"
                  @input="onSelectTrustedBorrower"
                />
              </template>
              <template #cell(actions)="{ item }">
                <validated-button
                  :action="async () => removeTrustedBorrower(item)"
                  :question="
                    $t('components.loanable.form.remove_member', { name: item.user.full_name })
                  "
                  :label="capitalize($t('forms.remove'))"
                  icon="trash"
                  size="sm"
                  variant="ghost-danger"
                />
              </template>
            </paginated-table>
          </conditional-context>
        </form-section>

        <template v-if="item.type === 'bike'">
          <form-section
            :force-show="openSections"
            :section-title="$t('components.loanable.form.bike_details')"
            :inititally-visible="!item.published"
            toggleable
          >
            <forms-validated-input
              v-model="item.details.model"
              :label="$t('loanables.fields.model') | capitalize"
              :rules="{
                required: item.published || validatePublishing,
              }"
              name="model"
              type="text"
            />

            <forms-validated-input
              v-model="item.details.bike_type"
              :label="$t('bikes.fields.bike_type.title') | capitalize"
              :rules="{
                required: item.published || validatePublishing,
              }"
              name="bike_type"
              type="select"
              :options="form.bike.bike_type.options"
            />

            <forms-validated-input
              v-model="item.details.size"
              :label="$t('bikes.fields.size.title') | capitalize"
              :rules="{
                required: item.published || validatePublishing,
              }"
              name="size"
              type="select"
              :options="form.bike.size.options"
            />
          </form-section>

          <form-section
            id="bike-report-section"
            :section-title="$t('components.loanable.form.bike_state.title')"
            :force-show="openSections"
            toggleable
            :inititally-visible="!item.published"
          >
            <loanable-form-bike-state
              :item="item"
              :validate-publishing="validatePublishing"
              :bike-form="bikeForm"
            />
          </form-section>
        </template>

        <template v-else-if="item.type === 'car'">
          <form-section
            :section-title="$t('components.loanable.form.car_details')"
            :force-show="openSections"
            :inititally-visible="!item.published"
            toggleable
          >
            <conditional-context
              show
              :label="$t('components.loanable.form.car_details_warning.label')"
              :additional-info="$t('components.loanable.form.car_details_warning.info')"
            >
              <forms-validated-input
                v-model="item.details.brand"
                :disabled="publishedFieldsDisabled"
                :label="$t('cars.fields.brand') | capitalize"
                :rules="{
                  required: validatePublishing,
                }"
                name="brand"
                type="text"
              />

              <forms-validated-input
                v-model="item.details.model"
                :disabled="publishedFieldsDisabled"
                :label="$t('loanables.fields.model') | capitalize"
                :rules="{
                  required: validatePublishing,
                }"
                name="model"
                type="text"
              />

              <forms-validated-input
                v-model="item.details.transmission_mode"
                :disabled="publishedFieldsDisabled"
                :label="$t('cars.fields.transmission_mode.title') | capitalize"
                :options="[
                  {
                    text: capitalize($t('cars.fields.transmission_mode.options.automatic')),
                    value: 'automatic',
                  },
                  {
                    text: capitalize($t('cars.fields.transmission_mode.options.manual')),
                    value: 'manual',
                  },
                ]"
                :rules="{
                  required: validatePublishing,
                  oneOf: ['manual', 'automatic'],
                }"
                name="transmission_mode"
                type="select"
              />

              <forms-validated-input
                v-model="item.details.engine"
                :disabled="publishedFieldsDisabled"
                :label="$t('cars.fields.engine.title') | capitalize"
                :options="[
                  { text: capitalize($t('cars.fields.engine.options.fuel')), value: 'fuel' },
                  { text: capitalize($t('cars.fields.engine.options.diesel')), value: 'diesel' },
                  {
                    text: capitalize($t('cars.fields.engine.options.electric')),
                    value: 'electric',
                  },
                  { text: capitalize($t('cars.fields.engine.options.hybrid')), value: 'hybrid' },
                ]"
                :rules="{
                  required: validatePublishing,
                  oneOf: ['fuel', 'diesel', 'electric', 'hybrid'],
                }"
                name="engine"
                type="select"
              />

              <forms-validated-input
                v-model="item.details.pricing_category"
                :disabled="publishedFieldsDisabled"
                :label="$t('cars.fields.pricing_category.title') | capitalize"
                :options="[
                  {
                    text: capitalize($t('cars.fields.pricing_category.options.car_small')),
                    value: 'small',
                  },
                  {
                    text: capitalize($t('cars.fields.pricing_category.options.car_large')),
                    value: 'large',
                  },
                  {
                    text: capitalize($t('cars.fields.pricing_category.options.car_small_electric')),
                    value: 'small_electric',
                  },
                  {
                    text: capitalize($t('cars.fields.pricing_category.options.car_large_electric')),
                    value: 'large_electric',
                  },
                ]"
                :rules="{
                  required: validatePublishing,
                  oneOf: ['small', 'large', 'small_electric', 'large_electric'],
                }"
                name="pricing_category"
                type="select"
              >
                <template #description>
                  {{ $t("components.loanable.form.pricing_category_description") }}
                  <a target="_blank" :href="$t('resources.pricings_url')">
                    {{ $t("components.loanable.form.pricing_category_link") }}
                  </a>
                </template>
              </forms-validated-input>
            </conditional-context>

            <forms-validated-input
              v-model="item.details.plate_number"
              :label="$t('cars.fields.plate_number') | capitalize"
              :rules="{
                required: validatePublishing,
              }"
              name="plate_number"
              type="text"
            />
          </form-section>

          <form-section
            id="report-section"
            :section-title="$t('components.loanable.form.car_state.title')"
            :force-show="openSections"
            toggleable
            :inititally-visible="!item.published"
          >
            <loanable-form-car-state :item="item" :validate-publishing="validatePublishing" />
          </form-section>

          <form-section
            :section-title="$t('components.loanable.form.insurance.title')"
            :force-show="openSections"
            toggleable
            :inititally-visible="!item.published"
          >
            <conditional-context
              show
              :label="$t('components.loanable.form.insurance.warning.label')"
              :additional-info="$t('components.loanable.form.insurance.warning.info')"
            >
              <forms-validated-input
                v-model="item.details.year_of_circulation"
                :description="$t('cars.fields.year_of_circulation.description')"
                :disabled="publishedFieldsDisabled"
                :label="$t('cars.fields.year_of_circulation.title') | capitalize"
                :min="1900"
                :max="nextYear"
                :rules="{
                  required: validatePublishing,
                  digits: 4,
                  min_value: 1900,
                  max_value: nextYear,
                }"
                name="year_of_circulation"
                type="number"
              />

              <forms-validated-input
                v-model="item.details.value_category"
                :disabled="publishedFieldsDisabled"
                :label="$t('cars.fields.value_category.title') | capitalize"
                :options="[
                  {
                    text: $t('cars.fields.value_category.options.lte50k'),
                    value: 'lte50k',
                  },
                  {
                    text: $t('cars.fields.value_category.options.lte70k'),
                    value: 'lte70k',
                  },
                  {
                    text: $t('cars.fields.value_category.options.lte100k'),
                    value: 'lte100k',
                  },
                ]"
                :rules="{
                  required: validatePublishing,
                  oneOf: ['lte50k', 'lte70k', 'lte100k'],
                }"
                name="value_category"
                type="select"
              />
            </conditional-context>

            <forms-validated-input
              v-model="item.details.has_informed_insurer"
              :label="$t('cars.fields.has_informed_insurer') | capitalize"
              name="has_informed_insurer"
              :rules="{
                ...carForm.has_informed_insurer.rules,
                accepted: item.published || validatePublishing,
              }"
              type="checkbox"
            >
              <template #label>
                <i18n path="components.loanable.form.insurance_coverage.text">
                  <template #url>
                    <a :href="$t('conditions.insurance_coverage_url')" download>{{
                      $t("components.loanable.form.insurance_coverage.url_text")
                    }}</a>
                  </template>
                </i18n>
              </template>
            </forms-validated-input>

            <forms-validated-input
              v-model="item.details.insurer"
              :label="$t('cars.fields.insurer') | capitalize"
              name="insurer"
              :rules="{ ...carForm.insurer.rules, required: item.published || validatePublishing }"
              type="suggest"
              :options="insurers"
            />

            <forms-validated-input
              v-model="item.details.papers_location"
              :label="$t('cars.fields.papers_location.title') | capitalize"
              :options="[
                {
                  text: $t('cars.fields.papers_location.options.in_the_car'),
                  value: 'in_the_car',
                },
                {
                  text: $t('cars.fields.papers_location.options.to_request_with_car'),
                  value: 'to_request_with_car',
                },
              ]"
              :rules="{
                required: item.published || validatePublishing,
                oneOf: ['in_the_car', 'to_request_with_car'],
              }"
              name="papers_location"
              type="select"
            />
          </form-section>

          <parametrizable-pricings-section
            :item="item"
            requires-pricing-parameters-file
            @update="setItemParameters($event)"
          />

          <form-section
            v-if="currentUserIsOwner"
            :section-title="$t('components.loanable.form.payment_section.title')"
            :inititally-visible="!item.published"
            toggleable
          >
            {{ $t("components.loanable.form.payment_section.description") }}
            <router-link to="/app">{{
              $t("components.loanable.form.payment_section.dashboard")
            }}</router-link
            >.
            <p class="mt-3">
              <b-link v-b-modal:bank-details-form>{{
                capitalize($t("components.loanable.form.payment_section.provide_bank_details"))
              }}</b-link>
            </p>
            <b-modal id="bank-details-form" hide-footer hide-header size="lg">
              <bank-details-form
                show-cancel
                @saved="() => $bvModal.hide('bank-details-form')"
                @cancel="() => $bvModal.hide('bank-details-form')"
              />
            </b-modal>
          </form-section>
        </template>

        <template v-else-if="item.type === 'car_trailer'">
          <form-section
            :section-title="$t('components.loanable.form.car_trailer_details')"
            :force-show="openSections"
            :inititally-visible="!item.published"
            toggleable
          >
            <b-alert variant="info" show>
              {{ $t("components.loanable.form.note") }}
            </b-alert>

            <forms-validated-input
              v-model="item.details.type"
              :disabled="publishedFieldsDisabled"
              :label="$t('car_trailers.fields.type.title') | capitalize"
              :options="[
                {
                  text: capitalize($t('car_trailers.fields.type.options.flatbed')),
                  value: 'flatbed',
                },
                {
                  text: capitalize($t('car_trailers.fields.type.options.enclosed')),
                  value: 'enclosed',
                },
                { text: capitalize($t('car_trailers.fields.type.options.dump')), value: 'dump' },
                { text: capitalize($t('car_trailers.fields.type.options.other')), value: 'other' },
              ]"
              :rules="{
                required: validatePublishing,
                oneOf: ['flatbed', 'enclosed', 'dump', 'other'],
              }"
              name="type"
              type="select"
            />

            <forms-validated-input
              v-model="item.details.type_details"
              :disabled="publishedFieldsDisabled"
              :label="$t('car_trailers.fields.type_details') | capitalize"
              name="type_details"
              type="text"
            />

            <forms-validated-input
              v-model="item.details.hitch_type"
              :disabled="publishedFieldsDisabled"
              :label="$t('car_trailers.fields.hitch_type.title') | capitalize"
              :options="[
                {
                  text: capitalize($t('car_trailers.fields.hitch_type.options.ball-1-7/8-inches')),
                  value: 'ball-1-7/8-inches',
                },
                {
                  text: capitalize($t('car_trailers.fields.hitch_type.options.ball-2-inches')),
                  value: 'ball-2-inches',
                },
                {
                  text: capitalize($t('car_trailers.fields.hitch_type.options.ball-2-5/16-inches')),
                  value: 'ball-2-5/16-inches',
                },
                {
                  text: capitalize($t('car_trailers.fields.hitch_type.options.other')),
                  value: 'other',
                },
              ]"
              :rules="{
                required: validatePublishing,
                oneOf: ['ball-1-7/8-inches', 'ball-2-inches', 'ball-2-5/16-inches', 'other'],
              }"
              name="hitch_type"
              type="select"
            />

            <forms-validated-input
              v-model="item.details.hitch_type_details"
              :disabled="publishedFieldsDisabled"
              :label="$t('car_trailers.fields.hitch_type_details') | capitalize"
              name="hitch_type_details"
              type="text"
            />

            <forms-validated-input
              v-model="item.details.electrical_connector_type"
              :disabled="publishedFieldsDisabled"
              :label="$t('car_trailers.fields.electrical_connector_type.title') | capitalize"
              :options="[
                {
                  text: capitalize(
                    $t('car_trailers.fields.electrical_connector_type.options.4-way-flat')
                  ),
                  value: '4-way-flat',
                },
                {
                  text: capitalize(
                    $t('car_trailers.fields.electrical_connector_type.options.4-way-round')
                  ),
                  value: '4-way-round',
                },
                {
                  text: capitalize(
                    $t('car_trailers.fields.electrical_connector_type.options.5-way-flat')
                  ),
                  value: '5-way-flat',
                },
                {
                  text: capitalize(
                    $t('car_trailers.fields.electrical_connector_type.options.6-way-round')
                  ),
                  value: '6-way-round',
                },
                {
                  text: capitalize(
                    $t('car_trailers.fields.electrical_connector_type.options.6-way-square')
                  ),
                  value: '6-way-square',
                },
                {
                  text: capitalize(
                    $t('car_trailers.fields.electrical_connector_type.options.7-way-round')
                  ),
                  value: '7-way-round',
                },
                {
                  text: capitalize(
                    $t('car_trailers.fields.electrical_connector_type.options.7-way-blades')
                  ),
                  value: '7-way-blades',
                },
              ]"
              :rules="{
                required: validatePublishing,
                oneOf: [
                  '4-way-flat',
                  '4-way-round',
                  '5-way-flat',
                  '6-way-round',
                  '6-way-square',
                  '7-way-round',
                  '7-way-blades',
                ],
              }"
              name="electrical_connector_type"
              type="select"
            />

            <forms-validated-input
              v-model="item.details.plate_number"
              :disabled="publishedFieldsDisabled"
              :label="$t('car_trailers.fields.plate_number') | capitalize"
              name="plate_number"
              type="text"
            />

            <forms-validated-input
              v-model="item.details.maximum_charge"
              :disabled="publishedFieldsDisabled"
              :label="$t('car_trailers.fields.maximum_charge') | capitalize"
              name="maximum_charge"
              type="text"
            />

            <forms-validated-input
              v-model="item.details.dimensions"
              :disabled="publishedFieldsDisabled"
              :label="$t('car_trailers.fields.dimensions') | capitalize"
              name="dimensions"
              type="text"
            />

            <forms-validated-input
              v-model="item.details.axle_number"
              :disabled="publishedFieldsDisabled"
              :label="$t('car_trailers.fields.axle_number.title') | capitalize"
              :options="[
                {
                  text: capitalize($t('car_trailers.fields.axle_number.options.one')),
                  value: 1,
                },
                {
                  text: capitalize($t('car_trailers.fields.axle_number.options.two')),
                  value: 2,
                },
                {
                  text: capitalize($t('car_trailers.fields.axle_number.options.three')),
                  value: 3,
                },
              ]"
              :rules="{
                required: validatePublishing,
                oneOf: [1, 2, 3],
              }"
              name="axle_number"
              type="select"
            />

            <forms-validated-input
              v-model="item.details.electric_brakes"
              :disabled="publishedFieldsDisabled"
              :label="$t('car_trailers.fields.electric_brakes.title') | capitalize"
              :options="[
                {
                  text: capitalize($t('car_trailers.fields.electric_brakes.options.true')),
                  value: true,
                },
                {
                  text: capitalize($t('car_trailers.fields.electric_brakes.options.false')),
                  value: false,
                },
              ]"
              :rules="{
                required: validatePublishing,
                oneOf: [true, false],
              }"
              name="electric_brakes"
              type="select"
            />
          </form-section>

          <parametrizable-pricings-section :item="item" @update="setItemParameters($event)" />
        </template>

        <form-section
          v-else-if="item.type === 'trailer'"
          :force-show="openSections"
          :section-title="$t('components.loanable.form.trailer_details')"
          :inititally-visible="!item.published"
          toggleable
        >
          <forms-builder v-model="item.details" :definition="form.trailer" entity="trailers" />
        </form-section>
        <div v-else class="form__section text-center">
          <span>{{ $t("components.loanable.form.select_type") }}</span>
        </div>

        <form-section
          :force-show="openSections"
          toggleable
          :section-title="$t('components.loanable.form.management_rights.title')"
        >
          <template v-if="item && item.library">
            <h3>{{ $t("components.loanable.form.fleet") }}</h3>
            <library-blurb :library="item.library" class="library-tag" />
          </template>

          <loanable-user-roles-list v-if="item.id" :loanable="item" />

          <b-alert v-else show variant="warning">
            {{ $t("components.loanable.form.management_rights.save_first") }}
          </b-alert>
        </form-section>

        <form-section
          toggleable
          class="form__section"
          :section-title="$t('components.loanable.form.availability.title')"
        >
          <a id="availability" />
          <loanable-availability-rules :changed="changed" :loanable="item" :loading="loading" />
        </form-section>

        <form-buttons v-if="!hideButtons">
          <template v-if="!item.published">
            <icon-button
              v-if="changed"
              role="send"
              :onclick="async () => saveAndPublish(validate)"
              :disabled="loading || publishing || wrongLoanableType"
            >
              {{ $t("components.loanable.form.buttons.save_and_publish") }}
            </icon-button>
            <icon-button
              v-else
              role="send"
              :onclick="async () => publish(validate)"
              :disabled="loading || publishing || wrongLoanableType"
            >
              {{ $t("components.loanable.form.buttons.publish") }}
            </icon-button>
            <b-icon
              v-if="wrongLoanableType"
              v-b-tooltip="
                $t('components.loanable.form.wrong_type_tooltip', {
                  community: currentUserIsOwner ? 'votre' : 'la',
                  owner_suffix: currentUserIsOwner ? '' : ' du propriétaire',
                })
              "
              scale="0.85"
              shift-v="5"
              icon="question-circle"
            />

            &nbsp;
            <icon-button
              variant="link"
              :disabled="!changed || loading"
              :onclick="() => saveWithoutPublishing(validate)"
            >
              {{ $t("components.loanable.form.buttons.save_later") | capitalize }}
            </icon-button>
          </template>
          <template v-else>
            <icon-button
              role="save"
              :onclick="async () => save(validate)"
              :disabled="!changed || loading"
            >
              {{ $t("forms.save") | capitalize }}
            </icon-button>
          </template>
          &nbsp;
          <icon-button
            v-if="showReset"
            role="reset"
            :disabled="!changed || loading"
            @click="$emit('reset')"
          >
            {{ $t("forms.reset") | capitalize }}
          </icon-button>
        </form-buttons>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import dayjs from "dayjs";

import AvailabilityStatus from "@/components/Loanable/AvailabilityStatus.vue";
import BankDetailsForm from "@/components/PaymentMethod/BankDetailsForm.vue";
import ConditionalContext from "@/components/shared/ConditionalContext.vue";
import FormButtons from "@/components/shared/FormButtons.vue";
import FormSection from "@/components/Loanable/FormSection.vue";
import FormsBuilder from "@/components/Forms/Builder.vue";
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import IconButton from "@/components/shared/IconButton.vue";
import LibraryBlurb from "@/components/Loanable/LibraryBlurb.vue";
import LoanableAvailabilityRules from "@/components/Loanable/AvailabilityRules.vue";
import LoanableFormBikeState from "@/components/Loanable/LoanableFormBikeState.vue";
import LoanableFormCarState from "@/components/Loanable/LoanableFormCarState.vue";
import LoanableTypeSelection from "@/components/Loanable/LoanableTypeSelection.vue";
import LoanableUserRolesList from "@/components/Loanable/UserRolesList.vue";
import ParametrizablePricingsSection from "@/components/Loanable/ParametrizablePricingsSection.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column } from "@/components/shared/PaginatedTableColumns";
import QuerySelect from "@/components/Forms/QuerySelect.vue";
import ValidatedButton from "@/components/shared/ValidatedButton.vue";

import { capitalize } from "@/helpers/filters";
import { extractMultipolygonPaths } from "@/helpers/geoJson";
import { getOwner, isAdminOfLoanable, isCoownerOrOwner } from "@/helpers/permissions/loanables";
import { canSeeDeletedUser, isGlobalAdmin } from "@/helpers/permissions/users";

import FormLabelsMixin from "@/mixins/FormLabelsMixin";
import FormMixin from "@/mixins/FormMixin";

import { put } from "@/requests/server";
import {
  addTrustedBorrower,
  removeTrustedBorrower,
  searchUsersAssignableToRole,
} from "@/requests/loanableUserRolesRequests";

export default {
  name: "LoanableForm",
  components: {
    AvailabilityStatus,
    BankDetailsForm,
    ConditionalContext,
    FormButtons,
    FormSection,
    FormsBuilder,
    FormsValidatedInput,
    IconButton,
    LibraryBlurb,
    LoanableAvailabilityRules,
    LoanableFormBikeState,
    LoanableFormCarState,
    LoanableTypeSelection,
    LoanableUserRolesList,
    PaginatedTable,
    ParametrizablePricingsSection,
    QuerySelect,
    ValidatedButton,
  },
  mixins: [FormLabelsMixin, FormMixin],
  props: {
    hideButtons: {
      type: Boolean,
      required: false,
    },
    showReset: {
      type: Boolean,
      required: false,
      default: false,
    },
    currentUserIsOwner: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      openSections: false,
      publishing: false,
      validatePublishing: false,
      insurers: [
        "Allstate",
        "Alpha Assurance",
        "Arar",
        "Aviva",
        "Banque Nationale Assurances",
        "Belairdirect",
        "CAA Québec",
        "CIBC",
        "Co-operators",
        "Desjardins Assurances",
        "Financière ScotiaVie",
        "iA Groupe financier",
        "Intact",
        "La Capitale",
        "PMT Roy",
        "Promutuel",
        "Prysm Assurances",
        "RBC",
        "RSA",
        "Sonnet",
        "SSQ",
        "TD Meloche Monnex",
        "Wawanesa",
      ],
    };
  },
  computed: {
    wrongLoanableType() {
      return !this.ownerAvailableTypes.includes(this.item?.type);
    },
    shareableCommunities() {
      return (this.item?.communities ?? []).map((community) => ({
        ...community,
        type: "community",
      }));
    },
    shareableLibrary() {
      if (!this.item?.library) {
        return null;
      }

      return {
        ...this.item.library,
        type: "library",
      };
    },
    shareableTargets() {
      return [
        ...this.shareableCommunities,
        ...(this.shareableLibrary ? [this.shareableLibrary] : []),
      ];
    },
    loanableCommunities() {
      // Only share loanable in communities in which owner is properly registered (not through friendship).
      return (this.ownerUser?.accessible_communities ?? [])
        .filter((cu) => cu.join_method !== "friend")
        .filter((cu) => cu.status !== "suspended")
        .map((c) => c.community);
    },
    user() {
      return this.$store.state.user;
    },
    loanablePolygons() {
      return this.loanableCommunities
        .filter((c) => !!c.area?.coordinates)
        .flatMap((c) => extractMultipolygonPaths(c.area));
    },
    guideUrl() {
      for (const community of this.loanableCommunities) {
        if (community.starting_guide_url) {
          return community.starting_guide_url;
        }
      }
      return null;
    },
    bikeForm() {
      const bikeKeys = Object.keys(this.form.bike);

      const form = {};

      // we add all the bike form property and our custom report_template property at the right time, see https://262.ecma-international.org/6.0/#sec-ordinary-object-internal-methods-and-internal-slots-ownpropertykeys
      for (let i = 0; i < bikeKeys.length; i++) {
        const key = bikeKeys[i];
        form[key] = this.form.bike[key];

        if (key === "report") {
          form["report_template"] = {};
        }
      }

      return form;
    },
    carForm() {
      const carKeys = Object.keys(this.form.car);

      const form = {};

      // we add all the car form property and our custom report_template property at the right time, see https://262.ecma-international.org/6.0/#sec-ordinary-object-internal-methods-and-internal-slots-ownpropertykeys
      for (let i = 0; i < carKeys.length; i++) {
        const key = carKeys[i];
        form[key] = this.form.car[key];

        if (key === "report") {
          form["report_template"] = {};
        }
      }

      return form;
    },
    ownerAvailableTypes() {
      return this.ownerUser?.available_loanable_types || [];
    },
    typeOptions() {
      return [
        {
          text: this.$t("components.loanable.form.type_options.placeholder"),
          disabled: true,
          value: null,
        },
        ...(this.form?.general.type.options || [])
          .filter((t) => this.ownerAvailableTypes.includes(t.value) || this.item?.type === t.value)
          .map((i) => ({
            ...i,
            text: capitalize(this.$i18n.t(`loanables.fields.types.${i.value}`)),
          })),
      ];
    },
    isAvailable() {
      return (
        !!this.item.id &&
        (this.item.availability_mode === "always" ||
          (this.item.availability_json && JSON.parse(this.item.availability_json).length > 0))
      );
    },
    nextYear() {
      return dayjs().add(1, "year").year();
    },
    ownerUser() {
      if (this.currentUserIsOwner) {
        return this.user;
      }

      // If loanable is not created yet, owner user can be in the loanable.owner_user field.
      if (!this.item) {
        return {};
      }

      if (!this.item.id && this.item.owner_user?.id) {
        return this.item.owner_user;
      }

      return getOwner(this.item) ?? {};
    },
    publishedFieldsDisabled() {
      return this.item?.published && !isGlobalAdmin(this.user);
    },
    showSelfServiceInput() {
      return (
        this.currentUserIsOwner ||
        isAdminOfLoanable(this.user, this.item) ||
        isCoownerOrOwner(this.user, this.item)
      );
    },
    ignoreFields() {
      let ignoreFields = ["merged_user_roles", "published", "availability_status"];

      if (this.item.type === "car" && this.publishedFieldsDisabled) {
        ignoreFields = [
          ...ignoreFields,
          "type",
          "details.pricing_category",
          "details.year_of_circulation",
          "details.transmission_mode",
          "details.engine",
          "details.value_category",
          "details.brand",
          "details.model",
        ];
      }
      return ignoreFields;
    },
    sharingModeOptions() {
      return [
        {
          value: "on_demand",
          text: capitalize(this.$t("loanables.fields.sharing_mode.options.on_demand")),
        },
        {
          value: "self_service",
          text: capitalize(this.$t("loanables.fields.sharing_mode.options.self_service")),
        },
        {
          value: "hybrid",
          text: capitalize(this.$t("loanables.fields.sharing_mode.options.hybrid")),
        },
      ];
    },
    trustedBorrowersColumns() {
      let trustedBorrowersColumns = [
        new Column("user.full_name", capitalize(this.$t("users.fields.full_name"))),
        Column.withRelation(
          "granted_by_user.full_name",
          capitalize(this.$t("user_roles.fields.granted_by")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            details: "email",
            params: { with_deleted: canSeeDeletedUser(this.user) },
          },
          "granted_by_user_id",
          { showByDefault: false }
        ),
      ];

      if (this.item && isAdminOfLoanable(this.user, this.item)) {
        trustedBorrowersColumns.push(
          new Column("user.phone", capitalize(this.$t("users.fields.phone")))
        );
        trustedBorrowersColumns.push(
          new Column("user.email", capitalize(this.$t("users.fields.email")))
        );
      }

      return trustedBorrowersColumns;
    },
    assignableUsersToRoleSearchFct() {
      const loanableId = this.item.id;
      const role = "trusted_borrower";

      let params = {
        per_page: 10,
      };

      return async function (q) {
        const data = await searchUsersAssignableToRole(loanableId, role, q, params);

        // Create options from returned data.
        return data.map((item) => {
          return {
            value: item.id,
            label: item.full_name,
            details: item.email,
            object: item,
          };
        });
      };
    },
  },
  watch: {
    ownerAvailableTypes(newTypes) {
      // For new loanables, we clear the loanable type selection if it is not valid when owner
      // is changed.
      if (this.item && !this.item.id && !newTypes.find((o) => o === this.item.type)) {
        this.item.type = null;
      }
    },
  },
  methods: {
    capitalize,
    itemLoadedCallback() {
      if (!this.item.id && this.currentUserIsOwner) {
        this.$store.commit("loanables/mergeItem", {
          owner_user: this.user,
        });
      }
    },
    async saveWithoutPublishing(validate) {
      this.publishing = false;
      this.validatePublishing = false;
      await this.$nextTick();
      await this.save(validate);
    },
    async saveAndPublish(validate) {
      this.validatePublishing = true;
      // Next tick so that the publishing state updates
      await this.$nextTick();

      const saved = await this.save(validate);
      if (saved) {
        await this.publish(validate);
      }
    },
    async publish(validate) {
      if (this.item.published || this.wrongLoanableType) {
        return;
      }

      this.validatePublishing = true;
      // Next tick so that the publishing state updates
      await this.$nextTick();
      const validForPublishing = await validate();
      if (!validForPublishing) {
        // this.publishing = false;
        // Next tick so that the UI catches up with validation errors
        await this.scrollToFirstError();
        return;
      }

      this.publishing = true;

      const { data } = await put(
        `/loanables/${this.item.id}/publish`,
        {},
        {
          notifications: {
            action: "publication",
            onSuccess: this.$t("components.loanable.form.notifications.vehicle_published"),
          },
          cleanupCallback: () => (this.publishing = false),
        }
      );

      this.item.published = true;
      this.item.availability_status = data.availability_status;
      this.publishing = false;
    },
    async save(validate) {
      this.openSections = false;
      const valid = await validate();
      if (!valid) {
        await this.scrollToFirstError();
        return false;
      }
      await this.submitLoanable();

      return true;
    },
    async submitLoanable() {
      const isNew = !this.item.id;
      await this.submit();

      if (isNew) {
        this.$store.commit("addNotification", {
          title: this.$t("components.loanable.form.notifications.vehicle_created"),
          variant: "success",
        });
      }

      this.$emit("submitted");
    },
    async scrollToFirstError() {
      await this.$nextTick();
      const invalidItems = document.getElementsByClassName("is-invalid");
      if (invalidItems.length > 0) {
        this.openSections = true;
        setTimeout(() => this.$scrollTo(invalidItems[0]), 250);
      }
    },
    async onSelectTrustedBorrower(option) {
      if (!this.item.id) {
        return;
      }

      if (option) {
        const user = option?.object;

        if (user?.id) {
          await addTrustedBorrower(this.item.id, user.id);

          this.$refs.trustedBorrowerSelect.reset();
          this.$refs.trustedBorrowerTable.refresh();
        }
      }
    },
    async removeTrustedBorrower(trustedBorrower) {
      await removeTrustedBorrower(trustedBorrower);
      this.$refs.trustedBorrowerTable.refresh();
    },
    setShareableCommunityShared(communityId, shared) {
      const communityIndex = (this.item.communities ?? []).findIndex(
        (community) => community.id === communityId
      );

      if (communityIndex === -1) {
        return;
      }

      this.$set(this.item.communities[communityIndex], "shared", shared);
    },
    setItemParameters(params) {
      const { pricingId, parameters } = params;
      this.$set(this.item.parameters, pricingId, parameters);
    },
  },
};
</script>

<style lang="scss">
.loanable-form {
  .add-user-input {
    min-width: 15rem;
  }
  .library-tag {
    padding-bottom: 1.5rem;
  }

  &__community-sharing {
    margin-bottom: 1.5rem;

    &__title {
      margin-bottom: 0.5rem;
    }

    &__item {
      align-items: center;
      display: flex;
      justify-content: space-between;
      gap: 1rem;
    }
  }
}
</style>
